/**
 * SigTDD Documentation Page — `/docs`.
 *
 * Comprehensive user guide for the time-distance diagram tool, adapted from
 * the legacy TDD 2013 user manual. Every section is real HTML at build time
 * (framing R1, task D.4): the accordion uses native `<details>`, so the text
 * is present without JavaScript and each item has a stable anchor.
 */

import Link from '../Link';
import { LEARN_ROUTE_LIST } from '../../lib/route';
import { DOCS_FAQ } from './faq';
import './DocsPage.css';

interface AccordionItemProps {
  /** Stable anchor id, e.g. `import-data` → `/docs#import-data` */
  value: string;
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

function AccordionItem({ value, title, icon, children }: AccordionItemProps) {
  return (
    <details className="accordion-item" id={value}>
      <summary className="accordion-trigger">
        <span className="accordion-title">
          {icon && <span className="accordion-icon">{icon}</span>}
          <h3>{title}</h3>
        </span>
        <svg
          className="accordion-chevron"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </summary>
      <div className="accordion-content">{children}</div>
    </details>
  );
}

// Icons as inline SVGs
const BookIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const RocketIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

const GridIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const ChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const HelpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const LayersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

interface DocsPageProps {
  onClose?: () => void;
}

export default function DocsPage({ onClose }: DocsPageProps) {
  return (
    <main className="docs-page">
      <div className="docs-container">
        {/* Close button if modal */}
        {onClose && (
          <button className="docs-close" onClick={onClose} aria-label="Close documentation">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        {/* Hero */}
        <header className="docs-hero">
          <div className="docs-hero-icon">
            <BookIcon />
          </div>
          <h1>SigTDD Documentation</h1>
          <p className="docs-hero-subtitle">
            The user guide for the time-distance (time-space) diagram tool for traffic signal coordination
          </p>
        </header>

        {/* Quick Links */}
        <nav className="docs-quick-links">
          <a href="#getting-started" className="docs-quick-link">
            <RocketIcon />
            <span className="docs-quick-link-title">Getting Started</span>
            <p>Quick start guide</p>
          </a>
          <a href="#timing-plans" className="docs-quick-link">
            <LayersIcon />
            <span className="docs-quick-link-title">Timing Plans</span>
            <p>Multiple plan variations</p>
          </a>
          <a href="#td-diagram" className="docs-quick-link">
            <ChartIcon />
            <span className="docs-quick-link-title">TD Diagram</span>
            <p>Understanding the display</p>
          </a>
          <a href="#data-entry" className="docs-quick-link">
            <GridIcon />
            <span className="docs-quick-link-title">Data Entry</span>
            <p>Entering intersection data</p>
          </a>
        </nav>

        {/* Introduction */}
        <section id="introduction" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <BookIcon />
            </div>
            <h2>What is a time-distance diagram?</h2>
          </div>

          <div className="docs-intro-content">
            <p>
              A <strong>time-distance diagram (TDD)</strong>, called a <strong>time-space diagram</strong> in
              North American practice, is the drawing traffic engineers use to analyse and optimise signal
              coordination along a corridor of intersections: distance on one axis, time on the other,
              the signal phases at each intersection drawn as bars, and vehicle progression as sloped lines.
              The longer explanation, with a worked diagram, is on the{' '}
              <Link to="/learn/time-distance-diagram" event="learn-open" source="docs">time-distance diagram</Link> page.
            </p>

            <div className="docs-concept-grid">
              <div className="docs-concept">
                <h3>Y-Axis: Distance</h3>
                <p>
                  Intersections are plotted vertically based on their cumulative distance along the
                  corridor, measured in metres from the first intersection.
                </p>
              </div>
              <div className="docs-concept">
                <h3>X-Axis: Time</h3>
                <p>
                  Time progresses horizontally in seconds, showing the signal cycle repeating across
                  the diagram width.
                </p>
              </div>
              <div className="docs-concept">
                <h3>Phase Bars</h3>
                <p>
                  Colored horizontal bars at each intersection show when each signal phase is active
                  (green, yellow, red periods).
                </p>
              </div>
              <div className="docs-concept">
                <h3>Travel Paths</h3>
                <p>
                  Diagonal lines represent vehicle progression. The slope indicates speed - steeper
                  lines mean slower travel.
                </p>
              </div>
            </div>

            <div className="docs-highlight">
              <strong>The Goal:</strong> By adjusting signal <em>offsets</em>, engineers can create
              a &quot;green wave&quot; - allowing vehicles travelling at the design speed to pass through
              consecutive green lights without stopping.
            </div>
          </div>
        </section>

        {/* Getting Started */}
        <section id="getting-started" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <RocketIcon />
            </div>
            <h2>Getting Started</h2>
          </div>

          <p className="docs-section-lead">
            A plan is built from an intersection table; the diagram is drawn from that table as you type.
            Start from a new plan, an imported file or one of the six example corridors.
          </p>

          <div className="accordion">
            <AccordionItem
              value="new-plan"
              title="Creating a new plan"
            >
              <ol className="docs-steps">
                <li>Click <strong>New</strong> in the toolbar to start a fresh plan</li>
                <li>Click <strong>Add Intersection</strong> (+ button) in the data grid</li>
                <li>Enter intersection parameters: name, distance, speeds, offset, and phase durations</li>
                <li>Repeat for each intersection in your corridor</li>
                <li>The TD Diagram updates automatically as you enter data</li>
              </ol>
              <p className="docs-tip">
                <strong>Tip:</strong> New intersections are added with default values (400m distance,
                60 km/h speed, 36s phases). Modify these to match your corridor design.
              </p>
            </AccordionItem>

            <AccordionItem
              value="import-data"
              title="Importing existing data"
            >
              <p>SigTDD supports importing data from multiple file formats:</p>
              <ul className="docs-list">
                <li><strong>CSV:</strong> Spreadsheet export with intersection data</li>
                <li><strong>JSON:</strong> SigTDD plan or corridor export</li>
                <li><strong>UTDF:</strong> Synchro Universal Traffic Data Format export</li>
              </ul>
              <ol className="docs-steps">
                <li>Click <strong>Import</strong> in the toolbar</li>
                <li>Select your file (format is auto-detected)</li>
                <li>The plan will be loaded and displayed immediately</li>
              </ol>

              <h4>CSV Format</h4>
              <pre className="docs-code">{`No.,Name,Dist N,V N,V S,OS,A,B,C,D,E,Dir
1,Intersection - 1,400,60,60,0,36,36,36,36,36,"NS,N,S,NA,NS"
2,Intersection - 2,400,60,60,24,36,36,36,36,36,"NS,N,S,NA,NS"
3,Intersection - 3,400,60,60,48,36,36,36,36,36,"NS,N,S,NA,NS"`}</pre>

              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Column</th>
                    <th>Description</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>No.</td><td>Intersection sequence number</td><td>-</td></tr>
                  <tr><td>Name</td><td>Intersection identifier</td><td>-</td></tr>
                  <tr><td>Dist N</td><td>Distance to next intersection</td><td>metres</td></tr>
                  <tr><td>V N</td><td>Design speed northbound</td><td>km/h</td></tr>
                  <tr><td>V S</td><td>Design speed southbound</td><td>km/h</td></tr>
                  <tr><td>OS</td><td>Signal offset</td><td>seconds</td></tr>
                  <tr><td>A-F</td><td>Phase durations</td><td>seconds</td></tr>
                  <tr><td>Dir</td><td>Phase directions (NS, N, S, NA)</td><td>-</td></tr>
                </tbody>
              </table>
            </AccordionItem>

            <AccordionItem
              value="export-data"
              title="Exporting your plan"
            >
              <p>Save your work in multiple formats:</p>
              <ul className="docs-list">
                <li>
                  <strong>CSV (Active Plan):</strong> Spreadsheet-compatible format for data
                  analysis or sharing with other tools. Exports only the current timing plan.
                </li>
                <li>
                  <strong>JSON (Active Plan):</strong> Single plan data in legacy format for
                  compatibility with older workflows.
                </li>
                <li>
                  <strong>Corridor JSON (All Plans):</strong> Complete corridor with all timing
                  plans in a single file. Best for backups and project sharing.
                </li>
                <li>
                  <strong>PDF:</strong> Printable document with diagram and data table for the
                  active plan.
                </li>
              </ul>
              <ol className="docs-steps">
                <li>Click <strong>Export</strong> in the toolbar</li>
                <li>Select your preferred format from the dropdown</li>
                <li>The file will download automatically</li>
              </ol>
            </AccordionItem>

            <AccordionItem
              value="auto-save"
              title="Auto-save and drafts"
            >
              <p>SigTDD automatically saves your work to your browser&apos;s local storage:</p>
              <ul className="docs-list">
                <li>Changes are saved automatically after 1 second of inactivity</li>
                <li>Your draft is restored when you return to the application</li>
                <li>No login required - data is stored locally in your browser</li>
              </ul>
              <p className="docs-warning">
                <strong>Important:</strong> Local storage is browser-specific. Use Export to create
                permanent backups or to transfer work between devices.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* Timing Plans */}
        <section id="timing-plans" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <LayersIcon />
            </div>
            <h2>Timing Plans</h2>
          </div>

          <div className="docs-intro-content">
            <p>
              A <strong>corridor</strong> can contain multiple <strong>timing plans</strong> - different
              signal timing variations for the same set of intersections. This allows you to model
              different scenarios such as AM peak, PM peak, off-peak, or weekend timing plans.
            </p>
          </div>

          <div className="accordion">
            <AccordionItem
              value="plan-concept"
              title="What are timing plans?"
            >
              <p>
                Each timing plan represents a complete set of signal timings for your corridor.
                All plans share the same <strong>intersection geometry</strong> (names, distances, speeds)
                but can have different:
              </p>
              <ul className="docs-list">
                <li><strong>Offsets</strong> - Different coordination patterns for each plan</li>
                <li><strong>Phase durations</strong> - Adjusted green times for different demand patterns</li>
                <li><strong>Phase directions</strong> - Different sequencing assignments</li>
              </ul>
              <div className="docs-example">
                <strong>Example:</strong> A corridor might have:
                <ul className="docs-list" style={{ marginTop: '0.5rem' }}>
                  <li><strong>AM Peak</strong> - Optimized for inbound (southbound) commuters</li>
                  <li><strong>PM Peak</strong> - Optimized for outbound (northbound) commuters</li>
                  <li><strong>Off-Peak</strong> - Balanced progression in both directions</li>
                </ul>
              </div>
            </AccordionItem>

            <AccordionItem
              value="plan-selector"
              title="Using the Plan Selector"
            >
              <p>
                The Plan Selector appears in the center of the toolbar when a corridor is loaded.
                It shows the current plan name and position (e.g., &quot;AM Peak - 1 of 3&quot;).
              </p>

              <h4>Navigation</h4>
              <ul className="docs-list">
                <li><strong>‹ / ›</strong> arrows - Navigate to previous/next plan</li>
                <li><strong>Click the plan name</strong> - Opens dropdown to select any plan</li>
                <li><strong>Double-click the plan name</strong> - Rename the current plan</li>
              </ul>

              <h4>Dropdown Menu</h4>
              <p>Click the plan name to open the dropdown menu with these options:</p>
              <ul className="docs-list">
                <li><strong>Plan list</strong> - Click any plan to switch to it</li>
                <li><strong>+ New Plan</strong> - Create a blank timing plan</li>
                <li><strong>⧉ Duplicate</strong> - Copy the current plan (useful for creating variations)</li>
                <li><strong>× Delete</strong> - Remove the current plan (requires confirmation)</li>
              </ul>

              <p className="docs-tip">
                <strong>Tip:</strong> Drag plans in the dropdown to reorder them. Use the ⠿ handle
                to drag.
              </p>
            </AccordionItem>

            <AccordionItem
              value="plan-create"
              title="Creating and duplicating plans"
            >
              <h4>Creating a New Plan</h4>
              <ol className="docs-steps">
                <li>Click the plan name in the toolbar to open the dropdown</li>
                <li>Click <strong>+ New Plan</strong></li>
                <li>A new plan is created with default phase timings</li>
                <li>Double-click the plan name to rename it</li>
              </ol>

              <h4>Duplicating an Existing Plan</h4>
              <ol className="docs-steps">
                <li>Navigate to the plan you want to copy</li>
                <li>Open the dropdown and click <strong>⧉ Duplicate</strong></li>
                <li>The new plan copies all timings from the current plan</li>
                <li>Modify the duplicate as needed</li>
              </ol>

              <p className="docs-tip">
                <strong>Tip:</strong> Duplicating is faster than creating from scratch when you
                want to make small adjustments to an existing timing pattern.
              </p>
            </AccordionItem>

            <AccordionItem
              value="plan-shared-data"
              title="Shared vs plan-specific data"
            >
              <p>Understanding what&apos;s shared and what&apos;s plan-specific:</p>

              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Shared?</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Corridor name</td><td>Yes</td><td>Same name for all plans</td></tr>
                  <tr><td>Intersection names</td><td>Yes</td><td>Location identifiers</td></tr>
                  <tr><td>Distances</td><td>Yes</td><td>Physical spacing between intersections</td></tr>
                  <tr><td>Design speeds</td><td>Yes</td><td>Target progression speeds</td></tr>
                  <tr><td>Offsets</td><td>No</td><td>Different per plan</td></tr>
                  <tr><td>Phase durations</td><td>No</td><td>Different per plan</td></tr>
                  <tr><td>Phase directions</td><td>No</td><td>Different per plan</td></tr>
                </tbody>
              </table>

              <p className="docs-note">
                When you edit intersection geometry (name, distance, speed), the change applies to
                all timing plans. When you edit signal timing (offset, phases), the change only
                affects the current plan.
              </p>
            </AccordionItem>

            <AccordionItem
              value="plan-export"
              title="Exporting with multiple plans"
            >
              <p>SigTDD provides different export options depending on your needs:</p>

              <h4>Single Plan Export</h4>
              <ul className="docs-list">
                <li><strong>CSV (Active Plan)</strong> - Exports only the currently selected plan</li>
                <li><strong>JSON (Active Plan)</strong> - Exports the current plan in legacy format</li>
              </ul>

              <h4>Full Corridor Export</h4>
              <ul className="docs-list">
                <li>
                  <strong>Corridor JSON (All Plans)</strong> - Exports the complete corridor with
                  all timing plans in a single file
                </li>
              </ul>

              <p className="docs-tip">
                <strong>Tip:</strong> Use <strong>Corridor JSON</strong> for backups or sharing
                complete projects. The file preserves all plans and can be imported back into SigTDD
                with everything intact.
              </p>

              <h4>PDF Export</h4>
              <p>
                PDF export includes the diagram and data table for the <strong>active plan only</strong>.
                To document multiple plans, export each plan as a separate PDF.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* TD Diagram */}
        <section id="td-diagram" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <ChartIcon />
            </div>
            <h2>TD Diagram</h2>
          </div>

          <p className="docs-section-lead">
            The diagram plots cumulative distance up the page and time across it. Phase bars show each
            intersection's signal sequence shifted by its offset; projection lines and bands show where
            vehicles at the design speed meet green.
          </p>

          <div className="accordion">
            <AccordionItem
              value="phase-bars"
              title="Phase bars"
            >
              <p>
                Phase bars are colored horizontal segments at each intersection showing the signal
                phases in sequence. Each phase ends with an amber (yellow) transition and a 1-second
                all-red clearance.
              </p>
              <p>
                Phase bar colors indicate the <strong>sequence direction</strong> assigned to each phase:
              </p>
              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Direction</th>
                    <th>Color</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>NB &amp; SB</td><td className="color-cyan">Cyan</td><td>Phase allows both northbound and southbound movements</td></tr>
                  <tr><td>NB only</td><td className="color-magenta">Purple</td><td>Phase allows northbound movements only</td></tr>
                  <tr><td>SB only</td><td className="color-green">Green</td><td>Phase allows southbound movements only</td></tr>
                  <tr><td>NA</td><td style={{ color: '#888' }}>Grey</td><td>Phase not sequenced (e.g. pedestrian or turn phase)</td></tr>
                </tbody>
              </table>
              <p>
                The <strong>offset</strong> value shifts the entire phase pattern horizontally for
                each intersection. This is the key parameter for creating green wave progression.
              </p>
              <p className="docs-tip">
                <strong>Tip:</strong> Right-click any phase cell in the data grid to change its
                direction. The default directions for new intersections are:
                A = NB &amp; SB, B = NB, C = SB, D = NA.
              </p>
            </AccordionItem>

            <AccordionItem
              value="travel-paths"
              title="Travel paths (projection lines)"
            >
              <p>
                Projection lines show vehicle progression through the corridor. The slope of each
                line represents speed:
              </p>
              <ul className="docs-list">
                <li><strong>Steeper line</strong> = slower speed (more time to cover distance)</li>
                <li><strong>Shallower line</strong> = faster speed (less time to cover distance)</li>
              </ul>
              <p>There are four types of projection lines (see Display Options):</p>
              <ul className="docs-list">
                <li><strong>Sequence band:</strong> Filled band showing the green wave through all intersections</li>
                <li><strong>Next Green band:</strong> Filled band for adjacent-pair coordination</li>
                <li><strong>Next lines:</strong> Grey travel path lines at design speed (one per second)</li>
                <li><strong>Phase Extents:</strong> Dashed start/end boundary projections</li>
              </ul>
              <p className="docs-tip">
                <strong>Goal:</strong> Adjust offsets so the Sequence band widens - this is the
                coordinated &quot;green wave&quot; where vehicles pass all intersections without stopping.
              </p>
            </AccordionItem>

            <AccordionItem
              value="display-options"
              title="Display options"
            >
              <p>Use the Display Options panel (below the data grid) to control what appears on the diagram.
                Hover over any toggle for a description.</p>
              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Toggle</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>NB/SB Sequence</td><td>Green wave band through ALL intersections at design speed</td></tr>
                  <tr><td>NB/SB Next Green</td><td>Band showing departures that arrive on green at the next intersection</td></tr>
                  <tr><td>NB/SB Next</td><td>Travel path lines at design speed (regardless of signal state)</td></tr>
                  <tr><td>NB/SB Phase Extents</td><td>Dashed lines projecting phase start (green) and end (red) boundaries</td></tr>
                  <tr><td>Phase Letter</td><td>Show phase letter labels (A, B, C...) on phase bars</td></tr>
                </tbody>
              </table>
              <p className="docs-note">
                Projection lines (Sequence, Next Green, Next, Phase Extents) only draw for phases
                that match the direction. For example, NB lines only appear during NB or NB &amp; SB phases.
              </p>
            </AccordionItem>

            <AccordionItem
              value="copy-canvas"
              title="Copying the diagram"
            >
              <p>
                Copy the diagram to your clipboard for pasting into Word, PowerPoint, or any other
                application:
              </p>
              <ul className="docs-list">
                <li><strong>Copy Dark:</strong> Copies the diagram with a dark background</li>
                <li><strong>Copy Light:</strong> Copies the diagram with a white background (ideal for printing and reports)</li>
              </ul>
              <p>
                Both buttons appear in the top-left area of the diagram canvas. The copy renders the
                full diagram at the current view size regardless of the export palette.
              </p>
              <p className="docs-tip">
                <strong>Tip:</strong> Use <strong>Copy Light</strong> when inserting into documents
                that will be printed, and <strong>Copy Dark</strong> for presentations with dark
                slide backgrounds.
              </p>
            </AccordionItem>

            <AccordionItem
              value="pan-zoom"
              title="Navigation (pan and zoom)"
            >
              <p>Navigate the diagram using standard controls:</p>
              <ul className="docs-list">
                <li><strong>Scroll wheel:</strong> Zoom in/out</li>
                <li><strong>Click and drag:</strong> Pan the view</li>
                <li><strong>Pinch gesture:</strong> Zoom on touch devices</li>
              </ul>
              <p className="docs-note">
                The diagram automatically scales based on your corridor length and cycle time.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* Data Entry */}
        <section id="data-entry" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <GridIcon />
            </div>
            <h2>Data Entry</h2>
          </div>

          <p className="docs-section-lead">
            Every value the diagram needs lives in the intersection table: spacing, design speeds, offset
            and phase durations. Edits apply immediately and are clamped to valid ranges.
          </p>

          <div className="accordion">
            <AccordionItem
              value="intersection-table"
              title="Intersection table"
            >
              <p>
                The data grid on the left displays all intersection parameters in an editable table:
              </p>
              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Description</th>
                    <th>Range</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>No.</td><td>Sequence number (auto-assigned)</td><td>1, 2, 3...</td></tr>
                  <tr><td>Name</td><td>Intersection identifier</td><td>Text</td></tr>
                  <tr><td>Dist N</td><td>Distance to next intersection north</td><td>50-5000m</td></tr>
                  <tr><td>V N</td><td>Design speed northbound</td><td>1-110 km/h</td></tr>
                  <tr><td>V S</td><td>Design speed southbound</td><td>1-110 km/h</td></tr>
                  <tr><td>OS</td><td>Offset (signal timing shift)</td><td>0 to cycle length</td></tr>
                  <tr><td>A-F</td><td>Phase durations</td><td>1-300s per phase</td></tr>
                </tbody>
              </table>
            </AccordionItem>

            <AccordionItem
              value="editing-cells"
              title="Editing cell values"
            >
              <ol className="docs-steps">
                <li>Click on any cell to select it</li>
                <li>Type the new value</li>
                <li>Press <kbd>Enter</kbd> to confirm, or <kbd>Escape</kbd> to cancel</li>
                <li>The diagram updates immediately</li>
              </ol>
              <p className="docs-tip">
                <strong>Validation:</strong> Values are automatically clamped to valid ranges. If
                you enter a value outside the allowed range, it will be adjusted to the nearest
                valid value.
              </p>
            </AccordionItem>

            <AccordionItem
              value="offset-slider"
              title="Offset slider"
            >
              <p>
                The offset slider below the diagram provides quick adjustment of the selected
                intersection&apos;s offset value:
              </p>
              <ul className="docs-list">
                <li>Click on an intersection row to select it</li>
                <li>Drag the slider to adjust the offset</li>
                <li>Watch the travel paths shift in real-time</li>
              </ul>
              <p>
                This is the fastest way to fine-tune signal coordination and visualize the effect
                on green band width.
              </p>
            </AccordionItem>

            <AccordionItem
              value="add-remove"
              title="Adding and removing intersections"
            >
              <h4>Adding Intersections</h4>
              <ul className="docs-list">
                <li>Click the <strong>+</strong> button in the data grid header</li>
                <li>A new row is added with default values</li>
                <li>Edit the values to match your intersection</li>
              </ul>

              <h4>Removing Intersections</h4>
              <ul className="docs-list">
                <li>Click the <strong>×</strong> button on the row you want to remove</li>
                <li>The intersection is deleted immediately</li>
              </ul>

              <p className="docs-warning">
                <strong>Note:</strong> There is no undo for deletion. Export your plan first if
                you want to preserve the current state.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* Import/Export */}
        <section id="import-export" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <UploadIcon />
            </div>
            <h2>Import &amp; Export</h2>
          </div>

          <p className="docs-section-lead">
            Corridors come in as CSV, SigTDD JSON, Corridor JSON or Synchro UTDF and go out as CSV, JSON,
            Corridor JSON or PDF. The formats below are the exact shapes the parsers read and write.
            Synchro users: the{' '}
            <Link to="/learn/synchro-utdf-import" event="learn-open" source="docs">UTDF import guide</Link>{' '}
            explains the field mapping.
          </p>

          <div className="accordion">
            <AccordionItem
              value="csv-format"
              title="CSV format specification"
              icon={<DownloadIcon />}
            >
              <p>CSV files should follow this format:</p>
              <pre className="docs-code">{`No.,Name,Dist N,V N,V S,OS,A,B,C,D,E,Dir
1,Main St & 1st Ave,400,60,60,0,36,36,36,36,36,"NS,N,S,NA,NS"
2,Main St & 2nd Ave,350,55,55,22,30,30,40,30,30,"NS,N,S,NA,NS"`}</pre>

              <h4>Column Requirements</h4>
              <ul className="docs-list">
                <li>First row must be headers</li>
                <li>Values can be quoted or unquoted</li>
                <li>Phase columns (A-F) are optional - missing phases default to 0</li>
                <li><strong>Dir</strong> column stores phase directions as comma-separated codes:
                  NS (both), N (northbound), S (southbound), NA (not sequenced)</li>
                <li>Dir column is optional - if missing, positional defaults are applied
                  (1st=NS, 2nd=N, 3rd=S, 4th=NA)</li>
                <li>Column names are flexible (e.g., &quot;DistN&quot;, &quot;Dist_N&quot;, &quot;Distance&quot; all work)</li>
              </ul>
            </AccordionItem>

            <AccordionItem
              value="json-format"
              title="JSON format specification (legacy)"
              icon={<DownloadIcon />}
            >
              <p>Legacy JSON export includes a single plan structure:</p>
              <pre className="docs-code">{`{
  "id": "plan-uuid",
  "name": "Corridor Plan",
  "createdAt": "2026-02-21T10:00:00Z",
  "updatedAt": "2026-02-21T12:30:00Z",
  "intersections": [
    {
      "id": "int-uuid",
      "sequenceNo": 1,
      "name": "Main St & 1st Ave",
      "distanceToNext": 400,
      "speedNorth": 60,
      "speedSouth": 60,
      "offset": 0,
      "phases": [
        { "id": "A", "duration": 36, "direction": "NBSB" },
        { "id": "B", "duration": 36, "direction": "NB" }
      ]
    }
  ],
  "settings": { ... }
}`}</pre>
              <p className="docs-note">
                This format only supports a single timing plan. For multiple plans, use Corridor JSON.
              </p>
            </AccordionItem>

            <AccordionItem
              value="corridor-format"
              title="Corridor JSON format specification"
              icon={<DownloadIcon />}
            >
              <p>Corridor JSON includes all timing plans in a single file:</p>
              <pre className="docs-code">{`{
  "schemaVersion": 2,
  "format": "corridor",
  "id": "corridor-uuid",
  "name": "Main Street Corridor",
  "intersections": [
    {
      "id": "int-uuid",
      "sequenceNo": 1,
      "name": "Main St & 1st Ave",
      "distanceToNext": 400,
      "speedNorth": 60,
      "speedSouth": 60
    }
  ],
  "plans": [
    {
      "id": "plan-1-uuid",
      "name": "AM Peak",
      "timings": [
        {
          "intersectionId": "int-uuid",
          "offset": 0,
          "phases": [
            { "id": "A", "duration": 40, "direction": "NBSB" }
          ]
        }
      ]
    },
    {
      "id": "plan-2-uuid",
      "name": "PM Peak",
      "timings": [ ... ]
    }
  ],
  "activePlanIndex": 0
}`}</pre>
              <p className="docs-tip">
                <strong>Key differences from legacy JSON:</strong>
                <ul className="docs-list" style={{ marginTop: '0.5rem' }}>
                  <li>Intersection geometry is separate from timing data</li>
                  <li>Multiple plans are stored in the <code>plans</code> array</li>
                  <li>Each plan has its own <code>timings</code> array with offsets and phases</li>
                </ul>
              </p>
            </AccordionItem>

            <AccordionItem
              value="utdf-import"
              title="Importing from Synchro (UTDF)"
              icon={<UploadIcon />}
            >
              <p>
                SigTDD can import timing plans from Synchro using the Universal Traffic Data
                Format (UTDF):
              </p>
              <ol className="docs-steps">
                <li>In Synchro, select <strong>File → Export → UTDF</strong></li>
                <li>Save the <code>.txt</code> or <code>.utdf</code> file</li>
                <li>In SigTDD, click <strong>Import</strong> and select the file</li>
                <li>SigTDD extracts intersection locations, distances, and phase timings</li>
              </ol>

              <h4>What&apos;s Imported</h4>
              <ul className="docs-list">
                <li>Signalized intersections (TYPE=1)</li>
                <li>Intersection coordinates (converted to distances)</li>
                <li>Cycle length and offsets from timeplan data</li>
                <li>Phase durations</li>
              </ul>

              <p className="docs-note">
                UTDF import works best with arterial corridors. Complex networks may require
                manual adjustment after import. Check the browser console for import warnings.
              </p>
            </AccordionItem>

            <AccordionItem
              value="legacy-import"
              title="Importing from legacy TDD"
              icon={<UploadIcon />}
            >
              <p>
                If you have data from the legacy TDD desktop application (VB6/VB.NET), you can
                convert it to SigTDD format:
              </p>
              <ol className="docs-steps">
                <li>In legacy TDD, export your plan using <strong>File → Export Table</strong></li>
                <li>This creates a CSV file compatible with SigTDD</li>
                <li>Import the CSV into SigTDD using the Import button</li>
              </ol>
              <p className="docs-note">
                The legacy <code>.tdd</code> project file format is not directly supported.
                Use the CSV export from the legacy application.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* Settings */}
        <section id="settings" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <SettingsIcon />
            </div>
            <h2>Settings &amp; Defaults</h2>
          </div>

          <p className="docs-section-lead">
            New intersections start from the defaults below; the interface is dark throughout, with a
            light diagram copy available for printed reports.
          </p>

          <div className="accordion">
            <AccordionItem
              value="default-values"
              title="Default parameter values"
            >
              <p>New intersections are created with these default values:</p>
              <table className="docs-table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Default</th>
                    <th>Min</th>
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Distance to next</td><td>400m</td><td>50m</td><td>5000m</td></tr>
                  <tr><td>Speed North</td><td>60 km/h</td><td>1 km/h</td><td>110 km/h</td></tr>
                  <tr><td>Speed South</td><td>60 km/h</td><td>1 km/h</td><td>110 km/h</td></tr>
                  <tr><td>Offset</td><td>0s</td><td>0s</td><td>cycle length</td></tr>
                  <tr><td>Phase duration</td><td>36s</td><td>1s</td><td>300s</td></tr>
                </tbody>
              </table>
            </AccordionItem>

            <AccordionItem
              value="appearance"
              title="Appearance"
            >
              <p>SigTDD uses a consistent dark interface for clear on-screen contrast.</p>
              <p>
                For reports and printing, use <strong>Copy Light</strong> to export only the
                diagram with a white background; the application interface remains dark.
              </p>
            </AccordionItem>
          </div>
        </section>

        {/* FAQ — rendered from DOCS_FAQ; the same array feeds the FAQPage structured data */}
        <section id="faq" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <HelpIcon />
            </div>
            <h2>Frequently Asked Questions</h2>
          </div>

          <p className="docs-section-lead">
            Short answers to the questions that come up most when coordinating a corridor. The{' '}
            <Link to="/learn/green-wave-offset" event="learn-open" source="docs">green wave offset</Link> page
            works the arithmetic through in full.
          </p>

          <div className="accordion">
            {DOCS_FAQ.map((item) => (
              <AccordionItem key={item.id} value={item.id} title={item.question}>
                <p>{item.answer}</p>
              </AccordionItem>
            ))}
          </div>
        </section>

        {/* Learn */}
        <section id="learn" className="docs-section">
          <div className="docs-section-header">
            <div className="docs-section-icon">
              <BookIcon />
            </div>
            <h2>Learn</h2>
          </div>
          <p className="docs-section-lead">
            Longer articles on the engineering behind the tool, each ending with the example open in the
            workspace.
          </p>
          <ul className="docs-list docs-learn-list">
            {LEARN_ROUTE_LIST.map((route) => (
              <li key={route.path}>
                <Link to={route.path} event="learn-open" source="docs">{route.label}</Link>
                {' — '}{route.learn?.summary}
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="docs-cta">
          <h2>Ready to start?</h2>
          <p>Open the tool with an example corridor loaded and draw your first time-distance diagram.</p>
          <Link to="/app" className="docs-cta-button">Open the tool</Link>
        </section>

      </div>
    </main>
  );
}
